
import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ExternalLink, Youtube, Clock, ChevronRight } from 'lucide-react';
import { VideoEvent } from '@/hooks/useVideoEvents';
import { Card, CardContent } from '@/components/ui/card';

interface VideoCardProps {
  event: VideoEvent;
  channelName: string;
  onVideoClick: (videoId?: string) => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ event, channelName, onVideoClick }) => {
  const handleExternalLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card's onClick
    window.open(`https://youtube.com/watch?v=${event.videoId}`, '_blank');
  };

  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onVideoClick(event.videoId);
  };

  return (
    <Card 
      className="calendar-event cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Video thumbnail */}
      {event.videoId && (
        <div className="relative w-full h-48 bg-slate-200 dark:bg-gray-700">
          <img 
            src={`https://img.youtube.com/vi/${event.videoId}/hqdefault.jpg`} 
            alt={event.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = 'https://placehold.co/600x400?text=No+Thumbnail';
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-custom-primary dark:bg-custom-secondaryLight flex items-center justify-center">
              <div className="ml-1 w-0 h-0 border-t-[8px] border-b-[8px] border-l-[16px] border-r-0 border-solid border-t-transparent border-b-transparent border-l-white dark:border-l-custom-dark"></div>
            </div>
          </div>
        </div>
      )}
      
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-1">
          <h3 className="calendar-video-title">{event.title}</h3>
          <button 
            className="text-slate-400 hover:text-slate-600 dark:text-gray-400 dark:hover:text-gray-300"
            aria-label="External link"
            onClick={handleExternalLinkClick}
          >
            <ExternalLink size={16} />
          </button>
        </div>
        
        <div className="flex items-center text-sm social-profiles-text mb-2">
          <Youtube size={16} className="mr-1 text-custom-primary dark:text-custom-secondaryLight" />
          <span>{channelName || "Chargement de la chaîne..."}</span>
        </div>
        
        <div className="flex items-center calendar-event-time">
          <Clock size={14} className="mr-1" />
          <span>{format(event.date, 'dd MMM yyyy', { locale: fr })} à {event.time}</span>
        </div>
        
        <div className="mt-3 flex justify-between items-center">
          <button 
            className="text-custom-primary hover:text-custom-dark dark:text-custom-secondaryLight dark:hover:text-custom-secondaryLight/80 text-sm flex items-center font-sans"
            onClick={handleExternalLinkClick}
          >
            Consulter sur <Youtube size={16} className="ml-1 text-custom-primary dark:text-custom-secondaryLight" />
          </button>
          
          <div
            className="text-custom-primary hover:text-custom-dark dark:text-custom-secondaryLight dark:hover:text-custom-secondaryLight/80 text-sm flex items-center cursor-pointer font-sans"
          >
            Afficher dans la boîte de réception <ChevronRight size={16} className="ml-1" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VideoCard;
